/**
 * AllCashoutsPanel — full cashout history visible to cashiers and operators.
 * Shows Pending / Approved (cashier-paid) / Paid / Played Back tabs so staff
 * can see EVERY cashout on accounts they own and play any of them back —
 * including ones that were already paid at the counter.
 *
 * Designed to live below the existing PendingCashoutsPanel which still
 * highlights urgent items. This one is the audit/history tool.
 */
import { CashoutBreakdown } from './CashoutBreakdown';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import {
    History, Phone, User, Loader2, RefreshCw, Undo2, CheckCircle2, Clock, Wallet,
} from 'lucide-react';
import { PlayerName } from './PlayerDrilldownContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const QUICK_PLAY_BACK = [5, 10, 20, 50];

const TABS = [
    { key: 'all',         label: 'All',         tone: 'border-zinc-700 text-zinc-200' },
    { key: 'pending',     label: 'Pending',     tone: 'border-amber-500/40 text-amber-200' },
    { key: 'approved',    label: 'Approved',    tone: 'border-emerald-500/40 text-emerald-200' },
    { key: 'paid',        label: 'Paid',        tone: 'border-sky-500/40 text-sky-200' },
    { key: 'played_back', label: 'Played Back', tone: 'border-violet-500/40 text-violet-200' },
    { key: 'verify_hold', label: 'On Hold',     tone: 'border-orange-500/40 text-orange-200' },
];

const STATE_BADGE = {
    pending:     'bg-amber-500/15 text-amber-200 border-amber-500/40',
    approved:    'bg-emerald-500/15 text-emerald-200 border-emerald-500/40',
    paid:        'bg-sky-500/15 text-sky-200 border-sky-500/40',
    played_back: 'bg-violet-500/15 text-violet-200 border-violet-500/40',
    verify_hold: 'bg-orange-500/15 text-orange-200 border-orange-500/40',
};

const fmtAge = (iso) => {
    if (!iso) return '';
    const s = Math.max(1, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${Math.round(s)}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    if (s < 86400) return `${Math.round(s / 3600)}h`;
    return `${Math.round(s / 86400)}d`;
};

const AllCashoutsPanel = ({ role = 'cashier' }) => {
    const [status, setStatus] = useState('all');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [confirmFor, setConfirmFor] = useState(null);   // { row, amount } — for paid-cashout play-back
    const [confirmNote, setConfirmNote] = useState('');
    const [allCounts, setAllCounts] = useState(null);   // per-state counts from the last unfiltered load

    // Role drives which API namespace to hit. Both endpoints share the same
    // shape so the component logic is identical.
    const listEndpoint = role === 'admin'
        ? '/admin/cashouts/all'
        : '/cashier/cashouts/all';
    const playBackEndpoint = role === 'admin'
        ? '/admin/cashout-play-back'
        : '/cashier/cashout-playback';

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(
                `${API}${listEndpoint}?status=${status}&days=30`,
                { withCredentials: true }
            );
            setItems(data || []);
            if (status === 'all') {
                setAllCounts((data || []).reduce((acc, r) => {
                    const k = r.display_state || 'pending';
                    acc[k] = (acc[k] || 0) + 1;
                    acc.all = (acc.all || 0) + 1;
                    return acc;
                }, {}));
            }
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, [listEndpoint, status]);

    useEffect(() => {
        setLoading(true);
        load();
        const id = setInterval(load, 30_000);
        return () => clearInterval(id);
    }, [load]);

    const doPlayBack = async (row, amount) => {
        const isPaid = row.display_state === 'paid';
        // Paid cashouts always require explicit confirm — the cash is already
        // out the door so reversing it is a higher-stakes action.
        if (isPaid && confirmFor === null) {
            setConfirmFor({ row, amount });
            return;
        }
        setBusyId(`${row.id}-${amount ?? 'all'}`);
        try {
            const body = { transaction_id: row.id };
            if (amount != null) body.amount = amount;
            if (confirmNote) body.note = confirmNote;
            const { data } = await axios.post(`${API}${playBackEndpoint}`, body, { withCredentials: true });
            toast.success(
                isPaid
                    ? `$${data.refunded?.toFixed?.(2) ?? ''} returned from PAID cashout — credits restored to ${row.user_name}.`
                    : (data.message || 'Played back')
            );
            setConfirmFor(null);
            setConfirmNote('');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Play-back failed');
        } finally { setBusyId(null); }
    };

    const counts = items.reduce((acc, r) => {
        const k = r.display_state || 'pending';
        acc[k] = (acc[k] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-900/30" data-testid="all-cashouts-panel">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
                <div className="flex items-center gap-2">
                    <History className="w-4 h-4 text-zinc-400" />
                    <h2 className="text-sm font-semibold text-zinc-100">All cashouts (last 30 days)</h2>
                    {items.length > 0 && (
                        <span className="text-[10px] uppercase tracking-widest text-zinc-400 bg-white/5 border border-white/10 px-2 py-0.5 rounded">
                            {items.length}
                        </span>
                    )}
                </div>
                <Button onClick={load} size="sm" variant="outline"
                    className="h-8 border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                    data-testid="all-cashouts-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                </Button>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 px-4 py-2 border-b border-zinc-800 overflow-x-auto">
                {TABS.map((t) => {
                    const isActive = status === t.key;
                    const c = allCounts ? (allCounts[t.key] || 0) : (t.key === 'all' ? items.length : (counts[t.key] || 0));
                    return (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setStatus(t.key)}
                            className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded transition border flex items-center gap-1.5 ${
                                isActive
                                    ? `${t.tone} bg-white/5`
                                    : 'border-transparent text-zinc-500 hover:text-zinc-300'
                            }`}
                            data-testid={`all-cashouts-tab-${t.key}`}
                        >
                            {t.label}
                            <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 text-[9px]">
                                {c}
                            </span>
                        </button>
                    );
                })}
            </div>

            {loading ? (
                <div className="text-center py-6 text-zinc-500">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
                </div>
            ) : items.length === 0 ? (
                <p className="text-center text-zinc-500 py-6 text-sm">No cashouts found for this filter.</p>
            ) : (
                <div className="divide-y divide-zinc-800 max-h-[600px] overflow-y-auto">
                    {items.map((r, i) => {
                        const amt = Math.abs(Number(r.amount) || 0);
                        const state = r.display_state || 'pending';
                        // Backend only plays back the unpaid part of pending/approved rows.
                        const canPlayBack = (state === 'pending' || state === 'approved') && !r.paid;
                        const pbMax = r.breakdown?.unpaid_gross_credits ?? amt;
                        const isPaid = state === 'paid';
                        return (
                            <div key={r.id} className="p-3 space-y-2" data-testid={`all-cashouts-row-${i}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-medium text-zinc-100 flex items-center gap-1.5">
                                                <User className="w-3.5 h-3.5 text-zinc-500" />
                                                <PlayerName id={r.user_id} className="text-zinc-100 font-medium">
                                                    {r.user_name || '—'}
                                                </PlayerName>
                                            </span>
                                            <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${STATE_BADGE[state] || 'bg-zinc-800 text-zinc-300 border-zinc-700'}`}>
                                                {state === 'played_back'
                                                    ? (r.played_back_self_serve ? 'PLAYED BACK · CUSTOMER' : 'PLAYED BACK · STAFF')
                                                    : state.replace('_', ' ')}
                                            </span>
                                            {r.cashier_approved && (
                                                <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#39FF14]/15 text-[#39FF14] border border-[#39FF14]/40">
                                                    Counter · {r.cashier_name || 'cashier'}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5 flex-wrap">
                                            {r.user_phone && (
                                                <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.user_phone}</span>
                                            )}
                                            <span>via <span className="text-zinc-300">{r.payout_method || 'cash'}</span></span>
                                            {r.payout_handle && <span className="text-zinc-500">({r.payout_handle})</span>}
                                        </p>
                                        <p className="text-[10px] text-zinc-600 mt-0.5 flex items-center gap-2 flex-wrap">
                                            <span className="flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{fmtAge(r.created_at)} ago · {new Date(r.created_at).toLocaleString()}</span>
                                            {r.paid_at && (
                                                <span className="text-sky-300/70 flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5" />paid {fmtAge(r.paid_at)} ago</span>
                                            )}
                                            {r.played_back_at && (
                                                <span className="text-violet-300/70">played back {fmtAge(r.played_back_at)} ago</span>
                                            )}
                                        </p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className={`font-mono font-semibold text-lg ${isPaid ? 'text-sky-300' : state === 'played_back' ? 'text-violet-300' : 'text-amber-300'}`}>
                                            {fmt(isPaid && r.paid_total ? Number(r.paid_total) : (r.net_amount != null && state !== 'played_back' && state !== 'rejected' ? Number(r.net_amount) : (r.breakdown?.original_gross ?? amt)))}
                                        </p>
                                        {(state === 'played_back' || state === 'rejected') && (
                                            <p className="text-[10px] text-zinc-500 font-mono" data-testid={`all-cashouts-closed-${i}`}>requested · nothing left to send</p>
                                        )}
                                        {r.net_amount != null && state !== 'played_back' && state !== 'rejected' && (
                                            <p className="text-[10px] text-zinc-500 font-mono" data-testid={`all-cashouts-gross-${i}`}>
                                                {r.breakdown?.fee_retained === null ? 'sent (old gross basis)' : isPaid ? 'sent (net)' : 'send (net)'} · gross {fmt(amt)}
                                            </p>
                                        )}
                                        {state !== 'pending' && <CashoutBreakdown b={r.breakdown} testId={`all-cashouts-breakdown-${i}`} />}
                                        <p className="text-[10px] text-zinc-500 flex items-center gap-1 justify-end">
                                            <Wallet className="w-2.5 h-2.5" />Bal {fmt(r.user_balance)}
                                        </p>
                                    </div>
                                </div>

                                {canPlayBack && (
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium pr-1">
                                            {isPaid ? 'Reverse from paid:' : 'Play it back:'}
                                        </span>
                                        {QUICK_PLAY_BACK.filter(v => v <= pbMax).map(v => (
                                            <Button key={v} size="sm" variant="outline"
                                                disabled={busyId === `${r.id}-${v}`}
                                                onClick={() => doPlayBack(r, v)}
                                                className={`h-6 px-2 text-[11px] border-zinc-800 ${isPaid ? 'bg-sky-950/40 text-sky-200 hover:bg-sky-900/60' : 'bg-zinc-950 text-zinc-300 hover:bg-zinc-800'}`}
                                                data-testid={`all-cashouts-pb-${i}-${v}`}>
                                                <Undo2 className="w-2.5 h-2.5 mr-1" />${v}
                                            </Button>
                                        ))}
                                        <Button size="sm" variant="outline"
                                            disabled={busyId === `${r.id}-all`}
                                            onClick={() => doPlayBack(r, null)}
                                            className={`h-6 px-2 text-[11px] border-zinc-800 ${isPaid ? 'bg-sky-950/40 text-sky-200 hover:bg-sky-900/60' : 'bg-zinc-950 text-zinc-300 hover:bg-zinc-800'}`}
                                            data-testid={`all-cashouts-pb-${i}-all`}>
                                            <Undo2 className="w-2.5 h-2.5 mr-1" /> All {fmt(pbMax)}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Confirmation dialog for reversing a PAID cashout — extra friction
                because the cash is already out the door. */}
            <Dialog open={!!confirmFor} onOpenChange={(o) => { if (!o) { setConfirmFor(null); setConfirmNote(''); } }}>
                <DialogContent data-testid="all-cashouts-confirm-paid-dialog">
                    <DialogHeader>
                        <DialogTitle>Reverse a paid cashout?</DialogTitle>
                        <DialogDescription>
                            This will return <span className="font-mono text-emerald-300">{fmt(confirmFor?.amount || (confirmFor && Math.abs(Number(confirmFor.row?.amount) || 0)))}</span> back to{' '}
                            <span className="text-white">{confirmFor?.row?.user_name}</span>'s playable balance.
                            Make sure you've actually taken the cash back at the counter before confirming.
                        </DialogDescription>
                    </DialogHeader>
                    <Input
                        placeholder="Optional note (e.g. cash returned, player wants to keep playing)"
                        value={confirmNote}
                        onChange={(e) => setConfirmNote(e.target.value)}
                        className="bg-zinc-950 border-zinc-800 text-zinc-200"
                        data-testid="all-cashouts-confirm-note"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setConfirmFor(null); setConfirmNote(''); }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                const c = confirmFor;
                                setConfirmFor({ ...c, _confirmed: true });
                                // Bypass the gate by directly calling with confirmFor null
                                setTimeout(() => {
                                    setConfirmFor(null);
                                    setBusyId(`${c.row.id}-${c.amount ?? 'all'}`);
                                    const body = { transaction_id: c.row.id };
                                    if (c.amount != null) body.amount = c.amount;
                                    if (confirmNote) body.note = confirmNote;
                                    axios.post(`${API}${playBackEndpoint}`, body, { withCredentials: true })
                                        .then(({ data }) => {
                                            toast.success(`$${data.refunded?.toFixed?.(2) ?? ''} returned to ${c.row.user_name}'s balance.`);
                                            setConfirmNote('');
                                            load();
                                        })
                                        .catch((err) => toast.error(err.response?.data?.detail || 'Play-back failed'))
                                        .finally(() => setBusyId(null));
                                }, 0);
                            }}
                            className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-bold"
                            data-testid="all-cashouts-confirm-paid-btn"
                        >
                            Confirm reversal
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AllCashoutsPanel;
